/**
 * PrivateRentalsPage.jsx
 * Verified-users-only private rental search & booking page.
 * Accessible only via VerifiedRoute — auth + verification already confirmed.
 *
 * Features:
 *  - Date range picker → calls /vehicles/available?start=&end= for real-time availability
 *  - Fuel type filter (All / Electric / Hybrid / Gas)
 *  - Name search
 *  - Clean vehicle grid with "Start Booking" CTA
 */
import { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useApi } from '../context/AuthContext';
import { fleet as staticFleet } from '../data';
import {
  BoltIcon,
  FireIcon,
  MagnifyingGlassIcon,
  UsersIcon,
  CalendarDaysIcon,
  FunnelIcon,
} from '@heroicons/react/24/outline';

// ── Helpers ───────────────────────────────────────────────────────────────────

function apiVehicleToCard(v) {
  const isElectric = v.fuel?.toLowerCase().includes('electric') || v.teslaEnabled;
  const isHybrid   = v.fuel?.toLowerCase().includes('hybrid');
  const fuelLabel  = isElectric ? 'Electric' : isHybrid ? 'Hybrid' : (v.fuel || 'Gas');
  return {
    vin:      v.vin,
    name:     `${v.year} ${v.make} ${v.model}`,
    trim:     v.trim || '',
    seats:    v.seats || 5,
    fuel:     fuelLabel,
    fuelIcon: isElectric
      ? <BoltIcon className="w-4 h-4" />
      : <FireIcon className="w-4 h-4" />,
    desc:     v.description || '',
    price:    v.dailyRateCents ? Math.round(v.dailyRateCents / 100) : null,
    img:      v.imageUrl || '/images/model3.jpg',
    status:   v.status || 'available',
  };
}

function staticToCard(v) {
  const isElectric = v.fuel === 'Electric';
  const isHybrid   = v.fuel === 'Hybrid';
  return {
    vin:      v.vin || '',
    name:     v.name,
    trim:     v.trim || '',
    seats:    v.seats || 5,
    fuel:     v.fuel || 'Gas',
    fuelIcon: isElectric
      ? <BoltIcon className="w-4 h-4" />
      : <FireIcon className="w-4 h-4" />,
    desc:     v.desc || '',
    price:    v.price || null,
    img:      v.img || '/images/model3.jpg',
    status:   'available',
  };
}

function FuelBadge({ fuel }) {
  const styles = {
    Electric: 'bg-blue-100 text-blue-700',
    Hybrid:   'bg-green-100 text-green-700',
    Gas:      'bg-orange-100 text-orange-700',
  };
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${styles[fuel] || 'bg-gray-100 text-gray-600'}`}>
      {fuel}
    </span>
  );
}

function AvailabilityBadge({ status }) {
  if (status === 'available') {
    return <span className="inline-block px-2 py-0.5 rounded-full text-xs font-semibold bg-green-100 text-green-700">Available</span>;
  }
  if (status === 'rented') {
    return <span className="inline-block px-2 py-0.5 rounded-full text-xs font-semibold bg-yellow-100 text-yellow-700">Rented</span>;
  }
  return <span className="inline-block px-2 py-0.5 rounded-full text-xs font-semibold bg-red-100 text-red-700">Unavailable</span>;
}

// ── Today's date in YYYY-MM-DD for min attribute ──────────────────────────────
function todayStr() {
  return new Date().toISOString().split('T')[0];
}

// ── Main Component ────────────────────────────────────────────────────────────

export default function PrivateRentalsPage() {
  const navigate = useNavigate();
  const api = useApi();

  // ── All vehicles (loaded once) ────────────────────────────────────────────
  const [allVehicles, setAllVehicles] = useState([]);
  const [loadingVehicles, setLoadingVehicles] = useState(true);

  // ── Availability search ───────────────────────────────────────────────────
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate]     = useState('');
  const [availableVins, setAvailableVins] = useState(null); // null = no search yet
  const [searchLoading, setSearchLoading] = useState(false);
  const [searchError, setSearchError]     = useState('');

  // ── Filters ───────────────────────────────────────────────────────────────
  const [fuelFilter, setFuelFilter] = useState('All');
  const [nameSearch, setNameSearch] = useState('');

  // ── Load all vehicles on mount ────────────────────────────────────────────
  useEffect(() => {
    api.get('/vehicles')
      .then(r => {
        const items = Array.isArray(r.data) ? r.data : r.data?.vehicles || r.data?.items || [];
        if (items.length > 0) {
          setAllVehicles(items.map(apiVehicleToCard));
        } else {
          setAllVehicles(staticFleet.map(staticToCard));
        }
      })
      .catch(() => {
        setAllVehicles(staticFleet.map(staticToCard));
      })
      .finally(() => setLoadingVehicles(false));
  }, []);

  // ── Availability search ───────────────────────────────────────────────────
  const handleSearch = useCallback(async () => {
    if (!startDate || !endDate) {
      setSearchError('Please select both a start and end date.');
      return;
    }
    if (startDate >= endDate) {
      setSearchError('End date must be after start date.');
      return;
    }
    setSearchError('');
    setSearchLoading(true);
    try {
      const startISO = new Date(startDate + 'T00:00:00').toISOString();
      const endISO   = new Date(endDate   + 'T23:59:59').toISOString();
      const r = await api.get(`/vehicles/available?start=${encodeURIComponent(startISO)}&end=${encodeURIComponent(endISO)}`);
      const available = r.data?.available || [];
      setAvailableVins(new Set(available.map(v => v.vin)));
    } catch (e) {
      setSearchError('Could not check availability. Please try again.');
    } finally {
      setSearchLoading(false);
    }
  }, [startDate, endDate, api]);

  const clearSearch = () => {
    setStartDate('');
    setEndDate('');
    setAvailableVins(null);
    setSearchError('');
  };

  // ── Filtered vehicles ─────────────────────────────────────────────────────
  const filtered = allVehicles
    .filter(v => fuelFilter === 'All' || v.fuel === fuelFilter)
    .filter(v => !nameSearch || v.name.toLowerCase().includes(nameSearch.toLowerCase()))
    .map(v => ({
      ...v,
      // If a date search was done, override status based on availability result
      status: availableVins !== null
        ? (availableVins.has(v.vin) ? 'available' : 'rented')
        : v.status,
    }));

  const today = todayStr();

  return (
    <div className="min-h-screen bg-gray-50">

      {/* ── Page Header ──────────────────────────────────────────────────── */}
      <div className="bg-white border-b border-gray-200">
        <div className="max-w-6xl mx-auto px-4 py-8">
          <h1 className="text-3xl font-bold text-gray-900">Private Rentals</h1>
          <p className="mt-1 text-gray-500">
            Book directly with Drive Dobias — no third-party marketplace required.
          </p>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-4 py-8 space-y-6">

        {/* ── Search & Filter Bar ───────────────────────────────────────── */}
        <div className="bg-white rounded-2xl border border-gray-200 shadow-sm p-5">
          <div className="flex flex-col gap-4 lg:flex-row lg:items-end">

            {/* Date range */}
            <div className="flex flex-col sm:flex-row gap-3 flex-1">
              <div className="flex-1">
                <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">
                  <CalendarDaysIcon className="inline w-3.5 h-3.5 mr-1" />
                  Pick-up Date
                </label>
                <input
                  type="date"
                  min={today}
                  value={startDate}
                  onChange={e => { setStartDate(e.target.value); setAvailableVins(null); }}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
              <div className="flex-1">
                <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">
                  <CalendarDaysIcon className="inline w-3.5 h-3.5 mr-1" />
                  Return Date
                </label>
                <input
                  type="date"
                  min={startDate || today}
                  value={endDate}
                  onChange={e => { setEndDate(e.target.value); setAvailableVins(null); }}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
            </div>

            {/* Name search */}
            <div className="flex-1">
              <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">
                <MagnifyingGlassIcon className="inline w-3.5 h-3.5 mr-1" />
                Search
              </label>
              <div className="relative">
                <MagnifyingGlassIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                <input
                  type="text"
                  placeholder="e.g. Tesla, Sienna…"
                  value={nameSearch}
                  onChange={e => setNameSearch(e.target.value)}
                  className="w-full border border-gray-300 rounded-lg pl-9 pr-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
            </div>

            {/* Fuel filter */}
            <div>
              <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">
                <FunnelIcon className="inline w-3.5 h-3.5 mr-1" />
                Fuel Type
              </label>
              <div className="flex gap-2">
                {['All', 'Electric', 'Hybrid', 'Gas'].map(f => (
                  <button
                    key={f}
                    onClick={() => setFuelFilter(f)}
                    className={`px-3 py-2 rounded-lg text-sm font-medium border transition ${
                      fuelFilter === f
                        ? 'bg-blue-600 text-white border-blue-600'
                        : 'bg-white text-gray-600 border-gray-300 hover:border-blue-400'
                    }`}
                  >
                    {f}
                  </button>
                ))}
              </div>
            </div>

            {/* Check availability button */}
            <div className="flex gap-2">
              <button
                onClick={handleSearch}
                disabled={searchLoading || !startDate || !endDate}
                className="bg-blue-600 text-white px-5 py-2 rounded-lg text-sm font-semibold hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition whitespace-nowrap"
              >
                {searchLoading ? 'Checking…' : 'Check Availability'}
              </button>
              {availableVins !== null && (
                <button
                  onClick={clearSearch}
                  className="border border-gray-300 text-gray-600 px-4 py-2 rounded-lg text-sm font-medium hover:bg-gray-50 transition whitespace-nowrap"
                >
                  Clear
                </button>
              )}
            </div>
          </div>

          {/* Search error */}
          {searchError && (
            <p className="mt-3 text-sm text-red-600">{searchError}</p>
          )}

          {/* Availability result summary */}
          {availableVins !== null && !searchLoading && (
            <p className="mt-3 text-sm text-blue-700 font-medium">
              Showing availability for {startDate} → {endDate}.{' '}
              <span className="font-normal text-gray-500">
                {filtered.filter(v => v.status === 'available').length} of {filtered.length} vehicles available.
              </span>
            </p>
          )}
        </div>

        {/* ── Vehicle Grid ──────────────────────────────────────────────── */}
        {loadingVehicles ? (
          <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
            {[1, 2, 3].map(i => (
              <div key={i} className="animate-pulse bg-white rounded-2xl border border-gray-200 overflow-hidden">
                <div className="h-44 bg-gray-200" />
                <div className="p-5 space-y-3">
                  <div className="h-5 bg-gray-200 rounded w-3/4" />
                  <div className="h-4 bg-gray-200 rounded w-1/2" />
                  <div className="h-10 bg-gray-200 rounded mt-4" />
                </div>
              </div>
            ))}
          </div>
        ) : filtered.length === 0 ? (
          <div className="bg-white rounded-2xl border border-gray-200 p-10 text-center">
            <p className="text-gray-500 text-lg font-medium">No vehicles match your search.</p>
            <p className="text-gray-400 text-sm mt-1">Try adjusting your filters or clearing the date search.</p>
          </div>
        ) : (
          <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
            {filtered.map(car => {
              const isAvailable = car.status === 'available';
              return (
                <div
                  key={car.vin || car.name}
                  className={`bg-white rounded-2xl border overflow-hidden flex flex-col transition-shadow hover:shadow-md ${
                    isAvailable ? 'border-gray-200' : 'border-gray-100 opacity-70'
                  }`}
                >
                  {/* Image */}
                  <div className="relative">
                    <img
                      src={car.img}
                      alt={car.name}
                      className="w-full h-44 object-cover"
                    />
                    <div className="absolute top-3 right-3">
                      <AvailabilityBadge status={car.status} />
                    </div>
                  </div>

                  {/* Details */}
                  <div className="p-5 flex flex-col flex-grow">
                    {/* Name + trim */}
                    <div className="mb-3">
                      <h3 className="text-lg font-bold text-gray-900 leading-tight">{car.name}</h3>
                      {car.trim && <p className="text-xs text-gray-400 italic mt-0.5">{car.trim}</p>}
                    </div>

                    {/* Quick specs row */}
                    <div className="flex items-center gap-3 text-sm text-gray-600 mb-4">
                      <span className="flex items-center gap-1">
                        <UsersIcon className="w-4 h-4" />
                        {car.seats} seats
                      </span>
                      <span className="flex items-center gap-1">
                        {car.fuelIcon}
                        <FuelBadge fuel={car.fuel} />
                      </span>
                    </div>

                    {/* Spacer */}
                    <div className="flex-grow" />

                    {/* Price + CTA */}
                    <div className="flex items-center justify-between mt-2 pt-4 border-t border-gray-100">
                      <span className="text-lg font-bold text-blue-700">
                        {car.price ? `$${car.price}/day` : 'Contact for pricing'}
                      </span>
                      <button
                        onClick={() => navigate(`/book/${car.vin}`)}
                        disabled={!isAvailable || !car.vin}
                        className="bg-blue-600 text-white px-4 py-2 rounded-lg text-sm font-semibold hover:bg-blue-700 disabled:opacity-40 disabled:cursor-not-allowed transition"
                      >
                        {isAvailable ? 'Start Booking' : 'Unavailable'}
                      </button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* ── Help footer ───────────────────────────────────────────────── */}
        <div className="text-center py-6 text-sm text-gray-400">
          Questions about a vehicle or need a custom arrangement?{' '}
          <a href="/#contact" className="text-blue-600 hover:underline">Contact us</a>.
        </div>
      </div>
    </div>
  );
}
